import {
  AutocompleteInputChangeReason,
  debounce,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  Controller,
  FieldValues,
  useFormContext,
  Validate,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import WorkLocationAutocomplete from "../../../common/components/functional/autocomplete/workLocationAutocomplete/WorkLocationAutocomplete";
import CommonTextField from "../../../common/components/functional/commonTextField/CommonTextField";
import PhoneField from "../../../common/components/functional/phoneField/PhoneField";
import { compareByFieldFi } from "../../../common/helpers/common";
import { workLocationsAutoCompleteQuery } from "../customerAPI";
import { CustomerUser, WorkLocation } from "../types/queries";
import UserEmailAddress from "./UserEmailAddress";

type Props = {
  prefilledUser: CustomerUser | null;
  isCustomerUser?: boolean;
  userId?: string;
  clearEmailError: () => void;
  validateEmailAddress: Validate<string, FieldValues>;
  noBorder: boolean;
};

const AdditionalFields = ({
  prefilledUser,
  isCustomerUser,
  userId,
  clearEmailError,
  validateEmailAddress,
  noBorder,
}: Props) => {
  const [workLocationSearchString, setWorkLocationSearchString] = useState("");
  const [workLocationOptions, setWorkLocationOptions] = useState<any[]>([]); // TODO: duplicate WorkLocation types with different properties, fix this

  const { customerCompanyId } = useParams();

  const { t } = useTranslation();

  useEffect(() => {
    if (workLocationSearchString) {
      workLocationsAutoCompleteQuery({
        term: workLocationSearchString,
        customerCompanyId,
      }).then((res) => {
        setWorkLocationOptions(
          res.sort(compareByFieldFi((option) => option.customerOrgName ?? "")),
        );
      });
    }
  }, [workLocationSearchString, customerCompanyId]);

  const onWorkLocationInputChange = debounce(
    (term: string, reason?: AutocompleteInputChangeReason) => {
      if (reason === "input") {
        setWorkLocationSearchString(term);
      }
    },
    400,
  );

  const {
    control,
    trigger,
    formState: { errors },
  } = useFormContext();

  return (
    <>
      <Grid item xs={3} alignSelf="flex-start">
        <Typography
          sx={{ marginTop: "8px" }}
          color="textSecondary"
          align="right"
        >
          {t("common:email")}
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <UserEmailAddress
          prefilledUser={prefilledUser}
          userId={userId}
          clearEmailError={clearEmailError}
          validateEmailAddress={validateEmailAddress}
        />
      </Grid>
      <Grid item xs={3} alignSelf="center">
        <Typography color="textSecondary" align="right">
          {t("common:firstName")}
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <Controller
          name="firstName"
          control={control}
          rules={{
            required: true,
          }}
          render={({ field }) => (
            <CommonTextField
              {...field}
              fullWidth
              onBlur={() => trigger("firstName")}
              error={!!errors.firstName}
            />
          )}
        />
      </Grid>
      <Grid item xs={3} alignSelf="center">
        <Typography color="textSecondary" align="right">
          {t("common:lastName")}
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <Controller
          name="lastName"
          control={control}
          rules={{
            required: true,
          }}
          render={({ field }) => (
            <CommonTextField
              {...field}
              fullWidth
              onBlur={() => trigger("lastName")}
              error={!!errors.lastName}
            />
          )}
        />
      </Grid>
      <Grid item xs={3} alignSelf="center">
        <Typography color="textSecondary" align="right">
          {t("customers:usersNS:title")}
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <Controller
          name="title"
          control={control}
          render={({ field }) => <CommonTextField {...field} fullWidth />}
        />
      </Grid>
      <Grid item xs={3} alignSelf="center">
        <Typography color="textSecondary" align="right">
          {t("common:phone")}
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <PhoneField value={field.value} onChange={field.onChange} />
          )}
        />
      </Grid>
      {isCustomerUser && (
        <>
          <Grid item xs={3} alignSelf="center">
            <Typography color="textSecondary" align="right">
              {t("common:workLocation")}
            </Typography>
          </Grid>
          <Grid item xs={9}>
            <Controller
              name="customerUserWorkLocationIds"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange } }) => (
                <WorkLocationAutocomplete
                  multiple
                  value={value || []}
                  options={workLocationOptions}
                  getOptionDisabled={(option) => !option.active}
                  disableClearable
                  onChange={(newValue: WorkLocation[] | null) =>
                    onChange(newValue ?? [])
                  }
                  onInputChange={(val, reason) =>
                    onWorkLocationInputChange(val, reason)
                  }
                  required
                />
              )}
            />
          </Grid>
        </>
      )}
      {!noBorder && (
        <Grid item xs={12}>
          <Divider
            sx={{
              borderColor: "#4A1D9C1F",
              marginTop: "24px",
              marginBottom: "5px",
            }}
          />
        </Grid>
      )}
    </>
  );
};

export default AdditionalFields;
